import type { Rule } from '../../core/assetType.js';
import type { ValidationIssue } from '../../core/issues.js';
import type { Story } from '../../schemas/story.js';

import { asList, asRecord } from '../../activities/types.js';
import { locatorOf, toPointer } from '../../core/issues.js';

interface ActivityLike {
  type?: string;
  setup?: Record<string, unknown>;
}

const KNOWLEDGE_TEST_TYPES = new Set([
  'swipe_select',
  'drag_ordering',
  'drag_pairing',
  'phrase_matching',
]);

const optionList = (value: unknown): Record<string, unknown>[] => asList(value).map(asRecord);

/** The players finish with the option's outcomeId, and `complete` for an option without one. */
const addOptionOutcomes = (outcomes: Set<string>, options: Record<string, unknown>[]): void => {
  for (const option of options) {
    outcomes.add(typeof option.outcomeId === 'string' && option.outcomeId.length > 0 ? option.outcomeId : 'complete');
  }
  if (options.length === 0) {
    outcomes.add('complete');
  }
};

/**
 * Ports the JS validate-story.js `extractOutcomeIds` switch (lines 827–926).
 * Returns the set of outcome IDs an activity can emit, given its type and setup.
 */
export const extractOutcomeIds = (activity: ActivityLike | undefined | null): string[] => {
  const outcomes = new Set<string>();
  if (!activity || !activity.setup) {
    return Array.from(outcomes);
  }

  const setup = asRecord(activity.setup);
  const type = activity.type;

  if (setup.revealAnswer) {
    // Mirrors the player: phrase matching's reveal Continue finishes with the right option's outcomeId.
    const correctOption = type === 'phrase_matching'
      ? optionList(setup.options).find(o => o.value === setup.correctAnswer)
      : undefined;
    outcomes.add(typeof correctOption?.outcomeId === 'string' ? correctOption.outcomeId : 'complete');
    return Array.from(outcomes);
  }

  switch (type) {
    case 'dialogue':
    case 'pick_one': {
      for (const opt of optionList(setup.options)) {
        if (typeof opt.outcomeId === 'string') {
          outcomes.add(opt.outcomeId);
        }
        else if (typeof opt.id === 'string') {
          outcomes.add(opt.id);
        }
      }
      break;
    }

    case 'flip_cards': {
      if (setup.mode === 'routing') {
        for (const card of optionList(setup.cards)) {
          if (typeof card.outcomeId === 'string') {
            outcomes.add(card.outcomeId);
          }
        }
      }
      break;
    }

    case 'swipe_select':
    case 'scroll_card':
    case 'info_accordion':
    case 'image':
      outcomes.add('complete');
      break;

    case 'drag_pairing': {
      const isKnowledgeTest = asList(setup.correctAnswerOrder).length > 0;
      if (isKnowledgeTest && setup.requireCorrectAnswer === false) {
        outcomes.add('correct');
        outcomes.add('wrong');
      }
      else {
        outcomes.add('complete');
      }
      break;
    }

    case 'multi_choice_single_response': {
      // Mirrors the player: a right answer makes it a test, which finishes correct or wrong when
      // `requireCorrectAnswer` is false, and complete otherwise (it waits for the right answer).
      if (setup.correctAnswerSingle) {
        if (setup.requireCorrectAnswer === false) {
          outcomes.add('correct');
          outcomes.add('wrong');
        }
        else {
          outcomes.add('complete');
        }
      }
      else {
        // Mirrors the player: an option finishes with its outcomeId when set, else its value.
        const optValues = optionList(setup.options)
          .map(o => (typeof o.outcomeId === 'string' && o.outcomeId.length > 0 ? o.outcomeId : o.value))
          .filter((v): v is string => typeof v === 'string' && v.length > 0);
        if (optValues.length > 0) {
          for (const v of optValues) {
            outcomes.add(v);
          }
        }
        else {
          outcomes.add('complete');
        }
      }
      break;
    }

    case 'multi_choice_multi_response': {
      const isKnowledgeTest = asList(setup.correctAnswerMultiple).length > 0;
      if (isKnowledgeTest && setup.requireCorrectAnswer === false) {
        outcomes.add('correct');
        outcomes.add('wrong');
      }
      else {
        outcomes.add('complete');
      }
      break;
    }

    case 'start_activity':
      outcomes.add('start');
      break;

    case 'base_activity': {
      if (typeof setup.outcomeId === 'string') {
        outcomes.add(setup.outcomeId);
      }
      else { outcomes.add('default'); }
      break;
    }

    case 'phrase_matching': {
      // Mirrors the player: with a right answer and `requireCorrectAnswer: false` it finishes
      // correct or wrong; otherwise with the placed option's outcomeId, or complete.
      if (setup.correctAnswer && setup.requireCorrectAnswer === false) {
        outcomes.add('correct');
        outcomes.add('wrong');
        break;
      }
      addOptionOutcomes(outcomes, optionList(setup.options));
      break;
    }

    case 'drag_ordering': {
      const isKnowledgeTest = asList(setup.correctAnswerOrder).length > 0;
      if (isKnowledgeTest && setup.requireCorrectAnswer === false) {
        outcomes.add('correct');
        outcomes.add('wrong');
      }
      else {
        addOptionOutcomes(outcomes, optionList(setup.options));
      }
      break;
    }
  }

  return Array.from(outcomes);
};

export const outcomesMatchExitRoutes: Rule<Story> = {
  code: 'story.outcomes',
  check(input): ValidationIssue[] {
    const issues: ValidationIssue[] = [];

    const firstEnding = input.nodes.find(n => n.nodeType === 'ending');
    const firstEndingId = firstEnding?.id;

    input.nodes.forEach((node, nodeIdx) => {
      // The Story schema uses `activities` (array) but real story JSON uses
      // `activity` (single object) — the schema is .passthrough() so the
      // singular field rides along untyped. Access through unknown.
      const nodeUntyped = node as unknown as Record<string, unknown>;
      const activity = nodeUntyped.activity as ActivityLike | undefined;
      const routes = node.exitRoutes;
      if (!activity || !routes || node.nodeType !== 'activity') {
        return;
      }

      const activityOutcomes = extractOutcomeIds(activity);
      const exitRouteKeys = Object.keys(routes);
      const exitRouteKeySet = new Set(exitRouteKeys);

      for (const outcomeId of activityOutcomes) {
        if (exitRouteKeySet.has(outcomeId)) {
          continue;
        }

        const activityEmitsComplete = activityOutcomes.includes('complete')
          && (activity.type ? KNOWLEDGE_TEST_TYPES.has(activity.type) : false);

        const path: (string | number)[] = ['nodes', nodeIdx, 'exitRoutes', outcomeId];
        const baseMessage = `Node ${node.id}: activity emits '${outcomeId}' but exitRoutes has no matching key`;
        const message = activityEmitsComplete
          ? `${baseMessage}. Knowledge tests emit "complete", not "correct"/"incorrect"`
          : baseMessage;

        const issue: ValidationIssue = {
          code: 'story.outcomes.missingExitRoute',
          severity: 'error',
          message,
          path,
          pointer: toPointer(path),
          locator: locatorOf({ nodeId: node.id, branchId: node.branchId }),
          currentValue: outcomeId,
          constraint: { kind: 'shape', detail: 'activity outcome must have an exitRoute' },
        };
        if (firstEndingId) {
          issue.fix = {
            kind: 'replace',
            value: firstEndingId,
            rationale: `Added exit route in ${node.id}: ${outcomeId} -> ${firstEndingId}`,
          };
        }
        issues.push(issue);
      }

      const activityOutcomeSet = new Set(activityOutcomes);
      for (const routeKey of exitRouteKeys) {
        if (routeKey === 'default') {
          continue;
        }
        if (activityOutcomeSet.has(routeKey)) {
          continue;
        }

        const path: (string | number)[] = ['nodes', nodeIdx, 'exitRoutes', routeKey];
        issues.push({
          code: 'story.outcomes.unusedExitRoute',
          severity: 'warning',
          message: `Node ${node.id}: exitRoute '${routeKey}' has no corresponding activity outcome`,
          path,
          pointer: toPointer(path),
          locator: locatorOf({ nodeId: node.id, branchId: node.branchId }),
          currentValue: routeKey,
          constraint: { kind: 'shape', detail: 'exitRoute key has no matching outcome' },
          fix: {
            kind: 'remove',
            rationale: `Removed unused exit route in ${node.id}: ${routeKey}`,
          },
        });
      }
    });

    return issues;
  },
};
